import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import GearList from "@/components/GearList";
import ProjectImages from "@/components/project/ProjectImages";
import ProjectDocuments from "@/components/project/ProjectDocuments";
import CrewList from "@/components/project/CrewList";
import { ClipboardList, Users2, FileText, Image } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useState } from "react";
import { useTranslation } from 'react-i18next';

interface ProjectTabsProps {
  projectId: string;
  project: {
    budget_enabled?: boolean;
    documents?: { name: string; url: string }[];
    scout_photos?: string[];
    camera_enabled?: boolean;
    ge_enabled?: boolean;
  } | undefined;
  canEdit: boolean;
  isPublicView?: boolean;
}

export function ProjectTabs({ projectId, project, canEdit, isPublicView = false }: ProjectTabsProps) {
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState("gear");
  
  return (
    <div className="mb-20 sm:mb-32">
      <Tabs 
        defaultValue="gear" 
        className="space-y-6"
        onValueChange={value => setSelectedTab(value)}
      >
        <TabsList className="bg-white/5 border-white/10 backdrop-blur-sm w-full flex overflow-x-auto flex-nowrap scrollbar-none sticky top-0 z-10 shadow-lg">
          <TabsTrigger 
            value="gear" 
            className="flex-1 min-w-[70px] text-sm h-12 px-1 sm:px-4 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm transition-all hover:bg-white/10"
          >
            <div className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2">
              <ClipboardList className="h-4 w-4" />
              <span className="text-xs sm:text-sm">{t('project.tabs.gear')}</span>
            </div>
          </TabsTrigger>
          <TabsTrigger 
            value="crew" 
            className="flex-1 min-w-[70px] text-sm h-12 px-1 sm:px-4 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm transition-all hover:bg-white/10"
          >
            <div className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2">
              <Users2 className="h-4 w-4" />
              <span className="text-xs sm:text-sm">{t('project.tabs.crew')}</span>
            </div>
          </TabsTrigger>
          <TabsTrigger 
            value="images" 
            className="flex-1 min-w-[70px] text-sm h-12 px-1 sm:px-4 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm transition-all hover:bg-white/10"
          >
            <div className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2">
              <Image className="h-4 w-4" />
              <span className="text-xs sm:text-sm">{t('project.tabs.images')}</span>
            </div>
          </TabsTrigger>
          <TabsTrigger 
            value="documents" 
            className="flex-1 min-w-[70px] text-sm h-12 px-1 sm:px-4 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm transition-all hover:bg-white/10"
          >
            <div className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2">
              <FileText className="h-4 w-4" />
              <span className="text-xs sm:text-sm">Docs</span>
            </div>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="gear" className="mt-6 focus-visible:outline-none animate-fade-in">
          <GearList 
            projectId={projectId} 
            budgetEnabled={project?.budget_enabled} 
            cameraEnabled={project?.camera_enabled}
            geEnabled={project?.ge_enabled}
            isPublicView={isPublicView}
          />
        </TabsContent>

        <TabsContent value="crew" className="mt-6 focus-visible:outline-none animate-fade-in">
          <CrewList projectId={projectId} canEdit={canEdit} isPublicView={isPublicView} />
        </TabsContent>

        <TabsContent value="images" className="mt-6 focus-visible:outline-none animate-fade-in">
          <ProjectImages 
            projectId={projectId}
            initialPhotos={project?.scout_photos}
            canEdit={canEdit}
            isPublicView={isPublicView}
          />
        </TabsContent>

        <TabsContent value="documents" className="mt-6 focus-visible:outline-none animate-fade-in">
          <ProjectDocuments
            projectId={projectId}
            initialDocuments={project?.documents}
            canEdit={canEdit}
            isPublicView={isPublicView}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
